import type {
  BotConfigResponse,
  CustomKeyboardLayout,
  PreviewReply,
} from "@bale-bot/types";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { api, describeError } from "../services/api";
import MenuBuilder from "@/components/MenuBuilder";
import PageHeader from "@/components/PageHeader";

/** Stable empty layout, so an unset menu does not remount the builder. */
const EMPTY_LAYOUT: CustomKeyboardLayout = [];

export default function SettingsPage() {
  const [config, setConfig] = useState<BotConfigResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<PreviewReply | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  /** Preview always runs against the saved config, never the unsaved draft. */
  const runPreview = useCallback(async (call: () => Promise<PreviewReply>) => {
    setPreviewBusy(true);
    setPreviewError(null);
    try {
      setPreview(await call());
    } catch (cause) {
      setPreviewError(describeError(cause));
    } finally {
      setPreviewBusy(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    api
      .getSettings()
      .then((data) => {
        if (active) setConfig(data);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeError(cause));
      });
    return () => {
      active = false;
    };
  }, []);

  const patch = useCallback((changes: Partial<BotConfigResponse>) => {
    setConfig((current) => (current ? { ...current, ...changes } : current));
  }, []);

  /** Stable so preview state changes never re-render the menu builder. */
  const handleMenuChange = useCallback(
    (customMenu: CustomKeyboardLayout) => {
      patch({ customMenu });
    },
    [patch],
  );

  const save = async () => {
    if (!config) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await api.updateSettings({
        liveChatUrl: config.liveChatUrl,
        isRoundRobinActive: config.isRoundRobinActive,
        fallbackMessage: config.fallbackMessage,
        welcomeMessage: config.welcomeMessage,
        menu: config.menu,
        customMenu: config.customMenu,
      });

      setConfig(updated);
      setSaved(true);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setSaving(false);
    }
  };

  if (!config) {
    return (
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        {!error && <CircularProgress size={28} />}
      </Stack>
    );
  }

  return (
    <Stack spacing={3} className="stagger">
      <PageHeader
        title="Bot settings"
        description="Live configuration — changes apply to the next message, no redeploy."
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Typography variant="h6">Messages</Typography>
          <TextField
            label="Welcome message"
            value={config.welcomeMessage}
            onChange={(e) => patch({ welcomeMessage: e.target.value })}
            multiline
            minRows={2}
            fullWidth
          />
          <TextField
            label="Fallback message"
            helperText="Sent when the bot does not understand the input."
            value={config.fallbackMessage}
            onChange={(e) => patch({ fallbackMessage: e.target.value })}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </Paper>
      <MenuBuilder
        layout={config.customMenu ?? EMPTY_LAYOUT}
        onChange={handleMenuChange}
      />
      <Paper sx={{ p: 3, maxWidth: 380 }}>
        <Stack spacing={1.5}>
          <Typography variant="h6">Preview</Typography>
          <Typography variant="caption" color="text.secondary">
            Runs the real bot against the <strong>saved</strong> configuration —
            save first to try unsaved edits. Nothing is written to analytics.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Button
              size="small"
              variant="contained"
              disabled={previewBusy}
              onClick={() => runPreview(() => api.previewMessage("/start"))}
            >
              Start
            </Button>
            <Button
              size="small"
              disabled={previewBusy || (!preview && !previewError)}
              onClick={() => {
                setPreview(null);
                setPreviewError(null);
              }}
            >
              Reset
            </Button>
            {previewBusy && <CircularProgress size={18} />}
          </Stack>

          {previewError && <Alert severity="error">{previewError}</Alert>}

          {preview && (
            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: "background.default" }}
            >
              <Stack spacing={1.5}>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {preview.text || "(no text)"}
                </Typography>

                {preview.reply_markup?.inline_keyboard.map((row, rowIndex) => (
                  <Stack
                    key={`preview-row-${rowIndex}`}
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{ flexWrap: "wrap" }}
                  >
                    {row.map((button, buttonIndex) => {
                      const key = `preview-btn-${rowIndex}-${buttonIndex}`;
                      // A url button opens the link; it never fires a callback.
                      if (button.url) {
                        return (
                          <Button
                            key={key}
                            size="small"
                            variant="outlined"
                            href={button.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {button.text}
                          </Button>
                        );
                      }
                      const action = button.callback_data;
                      return (
                        <Button
                          key={key}
                          size="small"
                          variant="outlined"
                          disabled={previewBusy || !action}
                          onClick={() =>
                            action &&
                            runPreview(() => api.previewCallback(action))
                          }
                        >
                          {button.text}
                        </Button>
                      );
                    })}
                  </Stack>
                ))}

                <Typography variant="caption" color="text.secondary">
                  {preview.edited
                    ? "would edit the previous message"
                    : "would send a new message"}
                </Typography>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Paper>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Typography variant="h6">Account managers</Typography>

          <FormControlLabel
            control={
              <Switch
                checked={config.isRoundRobinActive}
                onChange={(e) =>
                  patch({ isRoundRobinActive: e.target.checked })
                }
              />
            }
            label="Round-robin across account managers"
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: -1.5 }}
          >
            When off, every inquiry goes to the first verified manager.
            Invite and verify managers on the Account managers page.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }} useFlexGap>
            <Typography variant="body2" color="text.secondary">
              {config.activeAccountManagers.length === 0
                ? "No verified managers in the rotation yet."
                : `${config.activeAccountManagers.length} verified manager${config.activeAccountManagers.length === 1 ? "" : "s"} in rotation:`}
            </Typography>
            {config.activeAccountManagers.map((username) => (
              <Chip key={username} size="small" label={`@${username}`} />
            ))}
          </Stack>

          <Button
            component={RouterLink}
            to="/account-managers"
            variant="outlined"
            size="small"
            sx={{ alignSelf: "flex-start" }}
          >
            Manage account managers
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Stack spacing={1.5}>
          <Typography variant="h6">Menu</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={config.menu.accountManagerEnabled}
                onChange={(e) =>
                  patch({
                    menu: {
                      ...config.menu,
                      accountManagerEnabled: e.target.checked,
                    },
                  })
                }
              />
            }
            label="Show “contact an account manager”"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.menu.liveChatEnabled}
                onChange={(e) =>
                  patch({
                    menu: { ...config.menu, liveChatEnabled: e.target.checked },
                  })
                }
              />
            }
            label="Show “live chat”"
          />
          <TextField
            label="Live chat URL"
            value={config.liveChatUrl}
            onChange={(e) => patch({ liveChatUrl: e.target.value })}
            placeholder="https://support.example.com/chat"
            fullWidth
          />
        </Stack>
      </Paper>

      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Last updated {new Date(config.updatedAt).toLocaleString()}
        </Typography>
      </Stack>

      <Snackbar
        open={saved}
        autoHideDuration={3000}
        onClose={() => setSaved(false)}
        message="Settings saved"
      />
    </Stack>
  );
}